import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Twitter, Pencil, Rocket, Coins, ExternalLink } from "lucide-react";
import { api, avatarUrl, logoUrl, shortAddr } from "@/lib/api";
import { useWallet } from "@/context/WalletContext";
import { fmtUsd, fmtSui, timeAgo } from "@/lib/format";
import CoinLogo from "@/components/CoinLogo";
import ReferralCard from "@/components/ReferralCard";
import ProfileBadges from "@/components/ProfileBadges";
import EditProfileModal from "@/components/EditProfileModal";

export default function Profile() {
  const { handle } = useParams();
  const { address } = useWallet();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [suiUsd, setSuiUsd] = useState(2.15);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/profile/${handle}`); setData(data); setNotFound(false); }
    catch (e) { setNotFound(true); }
  }, [handle]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/sui-price").then(({ data }) => setSuiUsd(data.usd)).catch(() => {}); }, []);

  if (notFound) return <div className="text-center py-24 text-muted-foreground">Profile not found. <Link to="/" className="text-[#1EC62E]">Back to board →</Link></div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">Loading profile…</div>;

  const owned = address && data.address && address.toLowerCase() === data.address.toLowerCase();
  const name = data.display_name || (data.username ? `@${data.username}` : shortAddr(data.address));
  const avatar = avatarUrl(data.avatar_path);

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6" data-testid="profile-page">
      <Link to="/" className="text-sm text-muted-foreground hover:text-white">← Back to board</Link>

      <div className="card-surface p-5 sm:p-6 mt-4">
        <div className="flex items-start gap-4 sm:gap-5 flex-wrap">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border border-white/10 bg-white/5 flex items-center justify-center shrink-0" data-testid="profile-avatar">
            {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-4xl">🙂</span>}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display font-extrabold text-2xl" data-testid="profile-name">{name}</h1>
              {data.username && <span className="font-mono text-sm text-[#1EC62E]">@{data.username}</span>}
            </div>
            <a href={`https://suiscan.xyz/mainnet/account/${data.address}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 font-mono text-xs text-muted-foreground hover:text-[#1EC62E]">
              {shortAddr(data.address)} <ExternalLink className="w-3 h-3" />
            </a>
            {data.bio && <p className="text-sm text-muted-foreground mt-2 max-w-lg">{data.bio}</p>}
            {data.badges?.length > 0 && <ProfileBadges badges={data.badges} className="mt-3" />}
            <div className="flex items-center gap-3 mt-3">
              {data.twitter && <a href={data.twitter} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-[#1EC62E]"><Twitter className="w-4 h-4" /></a>}
              {owned && (
                <button onClick={() => setEditOpen(true)} data-testid="edit-profile-button" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs font-bold hover:border-[#1EC62E]/50">
                  <Pencil className="w-3.5 h-3.5" /> Edit profile
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-6 sm:pl-4 sm:border-l border-white/10">
            <div>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"><Rocket className="w-3 h-3" /> Launched</div>
              <div className="font-mono font-extrabold text-xl mt-0.5" data-testid="profile-launched-count">{data.launched_count || 0}</div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"><Coins className="w-3 h-3" /> Creator fees</div>
              <div className="font-mono font-extrabold text-xl mt-0.5 text-[#FFB800]" data-testid="profile-creator-fees">{fmtSui(data.creator_fees_claimable_sui || 0, 3)} SUI</div>
            </div>
          </div>
        </div>
      </div>

      {owned && <div className="mt-5"><ReferralCard address={data.address} /></div>}

      <h2 className="font-display font-bold text-lg mt-8 mb-3">Coins launched</h2>
      {(data.launched || []).length === 0 ? (
        <div className="card-surface p-10 text-center text-muted-foreground text-sm">No coins launched yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="profile-coins">
          {data.launched.map((c) => (
            <Link to={`/coin/${c.curve_id}`} key={c.curve_id} className="card-surface p-4 flex items-center gap-3 hover:border-[#1EC62E]/40 transition-colors" data-testid={`profile-coin-${c.curve_id}`}>
              <CoinLogo coin={c} size={44} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="font-bold text-sm truncate">{c.name}</span><span className="font-mono text-xs text-[#1EC62E]">${c.symbol}</span>{c.migrated && <span className="text-[9px] px-1.5 py-0.5 rounded text-black" style={{ background: "linear-gradient(90deg,#00E5FF,#FF007A)" }}>Cetus</span>}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{c.created_at ? timeAgo(c.created_at) : ""}</div>
              </div>
              <div className="text-right font-mono">
                <div className="text-[10px] text-muted-foreground uppercase">Mkt cap</div>
                <div className="font-bold text-sm">{fmtUsd((c.market_cap_sui || 0) * suiUsd)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {editOpen && <EditProfileModal open={editOpen} profile={data} address={address} onClose={() => setEditOpen(false)} onSaved={() => load()} />}
    </div>
  );
}
